import type { DashboardFilters, WidgetConfig, WidgetSize, WidgetType } from "@astrum/shared";
import { saveBlob } from "./transport.js";

/** What each widget is called, and what it asks the person to choose. */
export const WIDGET_CATALOGUE: Record<WidgetType, { label: string; hint: string }> = {
  number_tile: { label: "Number", hint: "One headline count: open, overdue or completed tasks." },
  tasks_by: { label: "Tasks by", hint: "Tasks grouped by status, person, project, priority or a dropdown field." },
  completion_trend: { label: "Completion trend", hint: "Tasks created and completed per day." },
  workload: { label: "Workload", hint: "Planned hours against each person's weekly capacity." },
  time_logged: { label: "Time logged", hint: "Hours logged, by person or project." },
  budget_burn: { label: "Budget burn", hint: "Spend against budget. Shown to project owners, managers and admins." },
  goals_progress: { label: "Goals", hint: "Objectives and how far along they are." },
  project_health: { label: "Project health", hint: "Projects, worst health first." },
  task_list: { label: "Task list", hint: "A filtered list of tasks." },
};

export const SIZE_LABEL: Record<WidgetSize, string> = { S: "Small", M: "Medium", L: "Large" };

export const GROUP_LABEL: Record<string, string> = {
  status: "Status",
  assignee: "Assignee",
  project: "Project",
  priority: "Priority",
  custom_field: "Dropdown field",
};

export const METRIC_LABEL: Record<string, string> = { open: "Open tasks", overdue: "Overdue tasks", completed: "Completed tasks" };

/** A widget's own title, else its type's name. */
export const widgetTitle = (w: { title: string | null; type: WidgetType; config: WidgetConfig }): string => {
  if (w.title) return w.title;
  if (w.type === "number_tile") return METRIC_LABEL[(w.config as { metric: string }).metric] ?? "Number";
  if (w.type === "tasks_by") return `Tasks by ${(GROUP_LABEL[(w.config as { groupBy: string }).groupBy] ?? "").toLowerCase()}`;
  return WIDGET_CATALOGUE[w.type].label;
};

/** The filter bar as the query the data and export routes read. `filtered=1` says these replace the dashboard's defaults. */
export function filterQuery(filters: DashboardFilters, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams({ filtered: "1", ...extra });
  if (filters.teamId) params.set("teamId", filters.teamId);
  if (filters.projectIds?.length) params.set("projectIds", filters.projectIds.join(","));
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.assigneeId) params.set("assigneeId", filters.assigneeId);
  return params.toString();
}

export const sameFilters = (a: DashboardFilters, b: DashboardFilters): boolean =>
  JSON.stringify([a.teamId ?? "", a.projectIds ?? [], a.from ?? "", a.to ?? "", a.assigneeId ?? ""]) ===
  JSON.stringify([b.teamId ?? "", b.projectIds ?? [], b.from ?? "", b.to ?? "", b.assigneeId ?? ""]);

export const hoursLabel = (minutes: number): string => `${Math.round((minutes / 60) * 10) / 10} h`;

export const moneyLabel = (cents: number, currency: string | null): string =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: currency ?? "USD", maximumFractionDigits: 0 }).format(cents / 100);

/**
 * Save the first chart canvas inside `root` as a PNG.
 * Canvases are transparent, so it is painted onto the panel colour first;
 * otherwise the picture is dark axis text on nothing.
 */
export async function exportChartPng(root: HTMLElement, fileName: string): Promise<boolean> {
  const canvas = root.querySelector("canvas");
  if (!canvas || canvas.width === 0) return false;
  const out = document.createElement("canvas");
  out.width = canvas.width;
  out.height = canvas.height;
  const ctx = out.getContext("2d");
  if (!ctx) return false;
  ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue("--panel").trim() || "#ffffff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, "image/png"));
  if (!blob) return false;
  await saveBlob(blob, fileName);
  return true;
}
